/**
 * Static copy for the application status tracker steps (ACP-1040).
 *
 * Source of truth: "AccessCMS Application Status Descriptions" master doc
 * (Rebecca Gallagher, CEAMSProgram > Design-HCD). Each step has ONE static
 * description (what the step is / why it matters) — the live state is conveyed by
 * the step's badge + icon, NOT by changing this copy. Per the HCD/eng alignment
 * (Nicholas Collingham / Rebecca Gallagher, 2026-06-22): description = what the
 * step is, status = where it is (the badge). No per-state description text.
 *
 * Onboarding remains "TBD" in the master doc; the interim copy is a neutral
 * placeholder to be replaced once design finalizes the text.
 */

export type TrackerStepKey =
  | 'onboarding'
  | 'submit'
  | 'sponsor'
  | 'foreignNational'
  | 'security'
  | 'fingerprints'
  | 'eapp'
  | 'prescreening';

export interface TrackerStepContent {
  /** Step title, per the eNOVA onboarding spec / master doc headings. */
  title: string;
  /** Accordion body, one entry per paragraph. */
  paragraphs: string[];
  /** True while the master doc still lists this step's copy as "TBD". */
  pendingCopy?: boolean;
}

export const STEP_CONTENT: Record<TrackerStepKey, TrackerStepContent> = {
  eapp: {
    paragraphs: [
      'An electronic application (eApp) through the National Background Investigation Services (NBIS) may be needed as part of the background investigation. This means that you will need to provide personal, employment, and background details. A Division of Credentialing Operations (DCO) security specialist determines if you need to submit an eApp. If the eApp is needed, you must submit it within 7 days. This step will affect your access to Centers for Medicare & Medicaid Services (CMS) systems and buildings.',
    ],
    title: 'eApp Submission',
  },

  fingerprints: {
    paragraphs: [
      'Fingerprint submission may be required as part of the background investigation. A Division of Credentialing Operations (DCO) security specialist determines whether this requirement has already been met or is needed. If required, fingerprints must be submitted within 7 days, as this step impacts access to Centers for Medicare & Medicaid Services (CMS) systems and facilities.',
    ],
    title: 'Fingerprint Submission',
  },

  foreignNational: {
    paragraphs: [
      'Contractors who are not United States (U.S.) citizens undergo an additional review where information is evaluated by the Division of Strategic Information (DSI) and processed through the Foreign National Management System (FNMS). This review ensures compliance with federal security requirements before access to government systems, facilities, or information is granted.',
    ],
    title: 'Foreign National Review',
  },

  // PENDING: master doc lists Onboarding Initiated as TBD. Interim copy kept.
  onboarding: {
    paragraphs: ['Your onboarding process has been initiated. You will receive email notifications at key milestones.'],
    pendingCopy: true,
    title: 'Onboarding Initiated',
  },

  prescreening: {
    paragraphs: [
      'The Office of Security, Facility and Logistics Operations (OSFLO) conducts prescreening investigative checks to confirm eligibility for federal or contractor employment. A favorable determination results in the issuance of a Workforce ID and Personal Identity Verification (PIV) credential, which are required for access to Centers for Medicare & Medicaid Services (CMS) systems and facilities.',
    ],
    title: 'Prescreening Determination',
  },

  security: {
    paragraphs: [
      'The Office of Security, Facility and Logistics Operations (OSFLO) reviews the application. They can approve it, decline it, or ask for changes. If updates are needed, an email will be sent. Making updates quickly will help ensure a smooth onboarding process.',
    ],
    title: 'Security Review',
  },

  sponsor: {
    paragraphs: [
      'The submitted application is reviewed by a Federal Approver or Contracting Officer Representative (COR), who may approve, decline, or return it for updates. Notifications are provided if action is required, and timely responses help keep the onboarding process on track.',
    ],
    title: 'Sponsor Review',
  },

  submit: {
    paragraphs: [
      'The Enterprise Onboarding and Verification Application (eNOVA) collects the information needed to complete Form HHS-745 (HHS Identification Badge Request) and Form OF-306 (Declaration for Federal Employment). This information is used to determine eligibility for federal or contractor employment and to issue a Personal Identity Verification (PIV) Credential (if applicable). The application must be completed within 14 days of receiving the eNOVA invitation to avoid delays in onboarding.',
    ],
    title: 'Submit Application',
  },
};
